/**
 * Reviews Page
 * Unified page for viewing and managing reviews and appeals
 * Supports all roles: contractors, verifiers, quality inspectors
 */

import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  Paper
} from '@mui/material';
import { Star, Gavel, RateReview } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ReviewList from '../components/reviews/ReviewList';
import MyAppealsPanel from '../components/reviews/MyAppealsPanel';
import reviewService from '../services/reviewService';

const ReviewsPage = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    loadStats();
  }, [user]);

  const loadStats = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const statsData = await reviewService.getUserStats(user.id);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = () => {
    const labels = {
      'contractor': 'Contractor',
      'verifier': 'Verifier',
      'quality_inspector': 'Quality Inspector',
      'client': 'Client'
    };
    return labels[user?.role] || 'Professional';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Reviews & Appeals
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your reviews and review appeals
        </Typography>
      </Box>

      {/* Statistics */}
      {stats && !loading && (
        <Card sx={{ mb: 3, p: 3 }}>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {stats.averageRating ? stats.averageRating.toFixed(1) : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Rating
              </Typography>
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 600 }}>
                {stats.totalReviews}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Reviews
              </Typography>
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: 'success.main' }}>
                {stats.responseRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Response Rate
              </Typography>
            </Box>
            {stats.verifiedReviews > 0 && (
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 600, color: 'info.main' }}>
                  {stats.verifiedReviews}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verified Reviews
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<RateReview />} 
            label="My Reviews" 
            iconPosition="start"
          />
          <Tab 
            icon={<Gavel />} 
            label="My Appeals" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {tabValue === 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Reviews for {getRoleLabel()}
            </Typography>
            <ReviewList 
              contractorId={user?.id} 
              showAll={true}
              onReviewUpdate={loadStats}
            />
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              My Review Appeals
            </Typography>
            <MyAppealsPanel />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ReviewsPage;

